import { Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CheckoutState } from 'src/app/model/enums/checkout-state';
import { PaymentModel } from 'src/app/model/enums/payment-model';
import { Fee } from 'src/app/model/interfaces/fee';
import { LotteryClassTicketPrices } from 'src/app/model/interfaces/lottery-class-ticket-prices';
import { Order } from 'src/app/model/interfaces/order';
import { OrderDisplayData } from 'src/app/model/interfaces/order-display-data';
import { OrderService } from 'src/app/services';
import { OrderDisplayDataService } from 'src/app/services/order-display-data.service';
import { Share } from '../../../model/interfaces/share';
import { TicketPackageInstance } from '../../../model/interfaces/ticket-package-instance';
import { PaymentInterval } from '../../../model/types/payment-interval';
import { ConfirmRemoveModalComponent } from '../modals/confirm-remove-modal.component/confirm-remove-modal.component';
import { PricingUpdateComponent } from '../pricing-update.component/pricing-update.component';
import { TrackingFacadeService } from 'src/app/services/tracking/tracking-facade.service';

declare interface ShareGroup {
  name: string;
  shares: Share[];
  size: number;
}

declare interface PackageGroup {
  name: string;
  packageInstances: TicketPackageInstance[];
  shareGroups: { [key: string]: ShareGroup };
}

class OrderLine {
  public name: string;
  public topPrize: number;
  public minPrize: number;
  public items: Share[] | TicketPackageInstance[];
  public iconUrl: string;
  public price: LotteryClassTicketPrices;
}

@Component({
  selector: 'app-orderline-list',
  templateUrl: './orderline-list.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./orderline-list.component.scss'],
})
export class OrderlineListComponent {
  public order: Order;
  public packageOrderLines;
  public shareOrderLines;
  public itemCount: number;

  public pendingDeleteItem;
  public topPrize: number;
  public minPrize: number;
  public winChance: number;
  public locked = false;
  public fees: Fee[] = [];
  public priceChangeInEffect = false;
  public pricingUpdateExpanded = false;
  public pricePrMonth: number;
  public priceTotal: number;

  @Input() public checkoutState: CheckoutState;
  @Input('show-win-rates') public showWinRates = false
  @Input('navigate-checkout')
  public navigateCheckout: boolean;

  @Input() showFees: boolean;

  @Input('show-package-details')
  public showPackageDetails: boolean;

  @Input('show-per-month')
  public showPerMonth: boolean = false;

  @Input('price-factor')
  public classCount: PaymentInterval = 6;

  @Input('isBasketPage')
  public isBasketPage: boolean;

  @Input('showAddMore')
  public showAddMore: boolean;

  @Input('isCheckout')
  public isCheckout: boolean;

  @Input('payment-model')
  public paymentModel: PaymentModel = PaymentModel.Subscription;

  @Output() public onOpenClick = new EventEmitter();

  @ViewChild('pricingUpdate')
  public pricingUpdate: PricingUpdateComponent;

  @ViewChild('confirmRemoveModal')
  public confirmRemoveModal: ConfirmRemoveModalComponent;

  public orderLines: OrderLine[];
  public isBsAppActive: boolean;

  // Represents the first day of the five draw dates in the 1st possible class for an internet internet sale.
  public drawDateLast: Date;

  // Represents the last date of the five draw dates in the last class for an internet sale.
  public drawDateFirst: Date;

  private readonly orderService: OrderService;
  private readonly orderDisplayDataService: OrderDisplayDataService;
  private readonly trackingFacadeService: TrackingFacadeService;

  private readonly router: Router;
  private onOrderDisplayDataUpdated = new Subscription();
  private onOrderExpiredSubscription = new Subscription();
  private onOrderExpirationNoticeSubscription = new Subscription();
  private priceSubscription = new Subscription();

  constructor(
    orderService: OrderService,
    orderDisplayDataService: OrderDisplayDataService,
    router: Router,
    trackingFacadeService: TrackingFacadeService
  ) {
    this.router = router;
    this.orderService = orderService;
    this.orderDisplayDataService = orderDisplayDataService;
    this.trackingFacadeService = trackingFacadeService;
  }
  public ngOnInit(): void {
    this.orderDisplayDataService.setProperties(
      this.paymentModel,
      this.isBsAppActive,
      this.checkoutState
    );
    this.getOrderDisplayData();

    // this.update();
    this.setupSubscriptions();
  }

  private setupSubscriptions(): void {
    this.onOrderDisplayDataUpdated =
      this.orderDisplayDataService.onOrderDisplayDataUpdated.subscribe(
        (orderDisplayData: OrderDisplayData) => {
          this.updateDisplayData(orderDisplayData);
        }
      );

    this.onOrderExpiredSubscription = this.orderService.onOrderExpired.subscribe(() =>
      this.terminate()
    );
    this.onOrderExpirationNoticeSubscription = this.orderService.onOrderExpirationNotice.subscribe(
      () => this.warnTermination()
    );
  }
  private async getOrderDisplayData() {
    const displayData = await this.orderDisplayDataService.getOrderDisplayData();
    this.updateDisplayData(displayData);
  }

  private updateDisplayData(orderDisplayData: OrderDisplayData): void {
    try {
      this.topPrize = orderDisplayData.topPrize;
      this.minPrize = orderDisplayData.minPrize;
      this.winChance = orderDisplayData.winChance;
      this.priceTotal = orderDisplayData.priceTotal;
      this.order = orderDisplayData.order;
      this.orderLines = orderDisplayData.orderLines;
      this.packageOrderLines = (orderDisplayData.packageOrderLines ?? []).sort((a, b) => a.name.localeCompare(b.name));
      this.shareOrderLines = orderDisplayData.shareOrderLines;
      this.itemCount = orderDisplayData.itemCount;
      this.fees = orderDisplayData.fees;
      this.pricePrMonth = orderDisplayData.pricePrMonth;
      this.priceChangeInEffect = orderDisplayData.priceChangeInEffect;
    } catch (error) {
      console.error(error);
    }

  }

  public openConfirmRemoveModal(isPackage: boolean, data: any): void {
    this.trackingFacadeService.basketRemoveItemClick(isPackage);
    this.confirmRemoveModal.open(isPackage, data);
    window.setTimeout(() => {
      this.onOpenClick.emit();
    }, 400);
  }

  public deleteOrderItemShare(orderItem: Share): void {
    this.orderService.deleteShareFromOrder(orderItem).then(() => { });
    this.pendingDeleteItem = orderItem;
  }

  public deleteOrderItemPackage(orderItem: TicketPackageInstance): void {
    this.orderService.deletePackageFromOrder(orderItem).then(() => { });
    this.pendingDeleteItem = orderItem;
  }

  public handleConfirmedRemoval(data: { isPackage: boolean; data: any }): void {
    data.isPackage ? this.deleteOrderItemPackage(data.data) : this.deleteOrderItemShare(data.data);
    this.confirmRemoveModal.closeModal();
  }

  public goToPage(url: string): void {
    this.router.navigateByUrl(`/lodder-til-salg${url}`);
  }

  /**
   * Handles the "Tilføj flere" CTA click: tracks the click then navigates to the ticket list.
   */
  public onAddMoreClick(): void {
    this.trackingFacadeService.basketAddMoreClick();
    this.goToPage('/lodder');
  }

  public onPricingDetailsClick(): void {
    this.pricingUpdate.expand();
  }

  private terminate(): void {
    this.order = null;
  }

  private warnTermination(): void {
    console.log('terminates in a short bit');
  }

  public ngOnDestroy(): void {
    this.onOrderDisplayDataUpdated.unsubscribe();
    this.onOrderExpiredSubscription.unsubscribe();
    this.onOrderExpirationNoticeSubscription.unsubscribe();
    this.priceSubscription.unsubscribe();
  }

  public sortSharesByTicketNumberDesc(shares: ReadonlyArray<Share>): Share[] {
    return [...shares].sort((a, b) => b.Ticket.TicketNumber - a.Ticket.TicketNumber);
  }
}
